'use client';

import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { trackEvent } from 'components/shared/UserAnalytics/funnelEvents';
import { ZapIcon } from 'components/ring-air/ZapIcon';
import { AE_DELIVERY_GTM_EVENTS, AE_EMIRATE_STORAGE_KEY } from './config';
import { AE_EMIRATES, aeEmirateGroupOf, parseAeEmirate } from './emirates';
import {
  resolveAeDeliveryWindow,
  type AeDeliveryWindowState,
} from './deliveryWindow';
import { useAeDeliveryEta } from './useAeDeliveryEta';
import { aeDeliveryEtaCopy, aeEmirateLabel } from './copy';
import styles from './index.module.css';

// The buy page remounts its provider tree once ~150-190ms after hydration
// (pre-existing page bug), which would fire SHOWN twice per page view.
// Module-scope timestamp dedupe: mounts closer together than this window
// count as one impression; a genuine client-side nav back to the page
// (always later than this) still re-fires.
const SHOWN_DEDUPE_MS = 2_000;
let shownLastFiredAt = 0;

/**
 * UAE delivery-promise card for /ae/ring/buy. Visually mirrors India's
 * DeliveryEtaBadge card (same grey chrome, zap mark, title typography) but
 * instead of a pincode input it carries a native emirate <select>: the copy
 * is the ops window rule for the chosen emirate's group, in the India-badge
 * sequence (copy first, input below). The backend "Dispatched by" estimate
 * is hidden on AE for now (here and at every other AE surface) — the window
 * copy is the only ETA. It is always present: time-based best-case copy on
 * first load (no emirate named), the chosen emirate's group rule after a
 * selection, and a flat next-day promise for emirates without same-day /
 * 4-hour service (Ajman, RAK, Fujairah, UAQ) — never "not available" copy.
 * Resolver failure (no Asia/Dubai Intl data) shows the lookup-error line. The zap
 * icon is the exact same component (size + lottie intro + reduced-motion
 * handling) as India's DeliveryEtaBadge, passed a constant playToken (only
 * the one-time intro plays), and is hidden on the error copy.
 */
export const AeDeliveryEtaCard = () => {
  const t = useTranslations('ring');
  const { selectedEmirate, setSelectedEmirate, group, windowState, hasError } =
    useAeDeliveryEta(true); // only mounted once the caller's AE gate passed

  useEffect(() => {
    // Fire once per page view, not once per window/emirate change — a
    // visitor who keeps the tab open across a window boundary shouldn't
    // re-fire the "shown" event, and the provider-remount double mount is
    // deduped by the module-scope timestamp above. The emirate is read
    // straight from sessionStorage: this child effect runs before the
    // provider's restore effect, so the context value is still null here.
    if (Date.now() - shownLastFiredAt < SHOWN_DEDUPE_MS) return;
    shownLastFiredAt = Date.now();
    let emirate: string | null = null;
    try {
      emirate = parseAeEmirate(sessionStorage.getItem(AE_EMIRATE_STORAGE_KEY));
    } catch {
      // private-mode sessionStorage access can throw — report null.
    }
    trackEvent(AE_DELIVERY_GTM_EVENTS.SHOWN, {
      deliveryWindow: windowState,
      emirate,
    });
  }, []);

  const handleEmirateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const next = parseAeEmirate(event.target.value || null);
    // Resolve the window for the NEW emirate's group — `windowState` from the
    // hook still reflects the previous selection during this handler, and the
    // two groups disagree between 13:00 and 17:00 GST.
    const nextGroup = aeEmirateGroupOf(next);
    let nextWindow: AeDeliveryWindowState;
    try {
      nextWindow = resolveAeDeliveryWindow(nextGroup);
    } catch {
      nextWindow = 'error';
    }
    trackEvent(AE_DELIVERY_GTM_EVENTS.EMIRATE_SELECTED, {
      emirate: next,
      previous: selectedEmirate,
      deliveryWindow: nextWindow,
      group: nextGroup,
    });
    setSelectedEmirate(next);
  };

  // Sequence mirrors India's DeliveryEtaBadge: title copy first, then the
  // input (here the emirate select). The window line is ALWAYS present:
  // before any selection it is purely time-based (best-case Dubai windows
  // via the null -> 'dubai' group default, no emirate named in the copy);
  // after a selection it is that emirate's group rule — ineligible emirates
  // get the flat next-day promise, never "not available".
  const titleContent = aeDeliveryEtaCopy(group, windowState, t);
  const showZap = !hasError;

  return (
    <div className={styles.badge}>
      <div className={styles.card}>
        <div className={styles.titleRow} aria-live="polite">
          {showZap ? <ZapIcon playToken={0} /> : null}
          <p className={styles.title}>
            <strong>{titleContent}</strong>
          </p>
        </div>
        <div className={styles.selectWrapper}>
          <select
            className={`${styles.select}${
              selectedEmirate ? '' : ` ${styles.selectEmpty}`
            }`}
            value={selectedEmirate ?? ''}
            onChange={handleEmirateChange}
            aria-label={t('ring.aeDelivery.selectEmiratePlaceholder')}
            data-testid="ae-emirate-select"
            data-buttontype="ae-emirate-select"
          >
            <option value="">
              {t('ring.aeDelivery.selectEmiratePlaceholder')}
            </option>
            {AE_EMIRATES.map((emirate) => (
              <option key={emirate} value={emirate}>
                {aeEmirateLabel(emirate, t)}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};
